import { Component, effect, inject, input, OnInit } from '@angular/core';
import { PropertyChargeSchedulesFilter } from '../../../../core/filters/property-charge-schedule.filter';
import { PropertyStatuses, ScheduleFrequency } from '../../../../core/enums/data.enums';
import { BalancesPermission } from '../../../../core/enums/permissions.enums';
import { FaDuotoneIconComponent } from "@fortawesome/angular-fontawesome";
import { ParentFocusDirective } from '../../../../core/directives/parent-focus.directive';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiSelectComponent } from "../../../../parts/multi-select/multi-select.component";
import { LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';

@Component({
  selector: 'ms-property-charge-schedule-search-filter',
  imports: [CommonModule, FormsModule, FaDuotoneIconComponent, ParentFocusDirective, MultiSelectComponent, DatePlaceholderDirective],
  templateUrl: './property-charge-schedule-search-filter.html'
})
export class PropertyChargeScheduleSearchFilter extends BaseSearchFilterComponent<PropertyChargeSchedulesFilter> implements OnInit {
  public localFilter = input<PropertyChargeSchedulesFilter | undefined>(undefined);
  public localStorageKey = input<string | undefined>(undefined);

  public selectPropertyStatusOptions: PropertyStatuses[] = [PropertyStatuses.Active, PropertyStatuses.Inactive];
  public selectScheduleFrequencyOptions = Object.values(ScheduleFrequency);
  public canViewDeletedSchedules = false;

  public nameInput = '';
  public propertyTitleInput = '';
  public propertyAddressInput = '';
  public lotNumberInput = '';
  public minModifiedDateInput = '';
  public maxModifiedDateInput = '';

  // Amount inputs (display values in major units/dollars)
  public minScheduledAmountDisplay: number | undefined = undefined;
  public maxScheduledAmountDisplay: number | undefined = undefined;

  public selectedPropertyStatus: PropertyStatuses[] = [];
  public selectedFrequencies: ScheduleFrequency[] = [];

  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);

  constructor(public userSessionService: UserSessionService) {
    super();
    // Reactively re-evaluate deleted-schedule access when auth state or active org changes,
    // so the option doesn't go stale on org switch or once the auth state finishes loading.
    effect(() => {
      const authState = this.authorizationStateService.authorizationState();
      const activeOrgId = this.userSessionService.activeOrganization()?.organizationId;
      this.applyDeletedScheduleAccess(authState ?? null, activeOrgId);
    });
  }

  private applyDeletedScheduleAccess(
    authState: ReturnType<AuthorizationStateService['authorizationState']> | null,
    activeOrgId: number | undefined
  ): void {
    this.canViewDeletedSchedules = authState
      ? this.authorizationService.hasPermission(authState, BalancesPermission.ReadDeleted, activeOrgId)
      : false;

    if (this.canViewDeletedSchedules) {
      this.selectPropertyStatusOptions = [PropertyStatuses.Active, PropertyStatuses.Inactive, PropertyStatuses.Deleted];
      this.activeFilter.includeDeletedSchedules = true;
    } else {
      this.selectPropertyStatusOptions = [PropertyStatuses.Active, PropertyStatuses.Inactive];
      this.activeFilter.includeDeletedSchedules = false;

      // Remove Deleted from any persisted filter statuses
      if (this.activeFilter.statuses?.includes(PropertyStatuses.Deleted)) {
        this.activeFilter.statuses = this.activeFilter.statuses.filter(s => s !== PropertyStatuses.Deleted);
        this.selectedPropertyStatus = [...this.activeFilter.statuses];
      }
    }
  }

  public override get activeFilter(): PropertyChargeSchedulesFilter {
    return this.localFilter() ?? this.userSessionService.propertyChargeSchedulesFilter;
  }

  protected override getDefaultFilter(): PropertyChargeSchedulesFilter {
    return ListConstants.PROPERTY_CHARGE_SCHEDULES_FILTER_DEFAULT();
  }

  protected override get preservedFilterKeys(): (keyof PropertyChargeSchedulesFilter)[] {
    return ['organizationIds'];
  }

  protected override get dropdownSelector(): string {
    return '#property-charge-schedule-search-filter-dropdown';
  }

  protected override applyAuthGatedFields(): void {
    const authState = this.authorizationStateService.authorizationState();
    const activeOrgId = this.userSessionService.activeOrganization()?.organizationId;
    this.applyDeletedScheduleAccess(authState ?? null, activeOrgId);
  }

  protected override beforeApply(): void {
    this.convertDisplayValuesToMinorUnits();
  }

  protected override syncStateFromFilter(): void {
    this.propertyTitleInput = '';
    this.propertyAddressInput = '';
    this.lotNumberInput = '';
    this.minScheduledAmountDisplay = undefined;
    this.maxScheduledAmountDisplay = undefined;
    this.initializeDateDisplayValues();
    this.selectedPropertyStatus = [...(this.activeFilter.statuses || [])];
    this.selectedFrequencies = [...(this.activeFilter.frequencies || [])];
  }

  public ngOnInit(): void {
    this.loadFromStorage();
    this.selectedPropertyStatus = [...(this.activeFilter.statuses || [])];
    this.selectedFrequencies = [...(this.activeFilter.frequencies || [])];
    this.initializeAmountDisplayValues();
    this.initializeDateDisplayValues();
    this.onApply();
  }

  private initializeAmountDisplayValues(): void {
    const filter = this.activeFilter;
    const minorUnits = this.userSessionService.currentMinorUnits();
    this.minScheduledAmountDisplay = filter.minimumScheduledAmount ? CurrencyUtilities.convertMinorUnitsToMajor(Number(filter.minimumScheduledAmount), minorUnits) : undefined;
    this.maxScheduledAmountDisplay = filter.maximumScheduledAmount ? CurrencyUtilities.convertMinorUnitsToMajor(Number(filter.maximumScheduledAmount), minorUnits) : undefined;
  }

  private initializeDateDisplayValues(): void {
    this.minModifiedDateInput = this.activeFilter.minimumLastModifiedDate ?? '';
    this.maxModifiedDateInput = this.activeFilter.maximumLastModifiedDate ?? '';
  }

  private convertDisplayValuesToMinorUnits(): void {
    const filter = this.activeFilter;
    const minorUnits = this.userSessionService.currentMinorUnits();
    filter.minimumScheduledAmount = this.minScheduledAmountDisplay != undefined ? String(CurrencyUtilities.convertMajorUnitsToMinor(Number(this.minScheduledAmountDisplay), minorUnits)) : undefined;
    filter.maximumScheduledAmount = this.maxScheduledAmountDisplay != undefined ? String(CurrencyUtilities.convertMajorUnitsToMinor(Number(this.maxScheduledAmountDisplay), minorUnits)) : undefined;
  }

  private loadFromStorage(): void {
    const storageKey = this.localStorageKey();
    if (storageKey && this.localFilter()) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const parsedFilter = JSON.parse(saved);
          const currentFilter = this.localFilter()!;
          const updatedFilter = { ...currentFilter, ...parsedFilter };
          Object.keys(updatedFilter).forEach(key => {
            (currentFilter as any)[key] = (updatedFilter as any)[key];
          });
        } catch (error) {
          console.error('Failed to load filter from localStorage:', error);
        }
      }
    }
  }

  protected override saveToStorage(): void {
    const storageKey = this.localStorageKey();

    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(this.activeFilter));
    } else if (!this.localFilter()) {
      this.userSessionService.saveToLocalStorage(
        LocalStorageKeyConstants.PROPERTY_CHARGE_SCHEDULES_FILTER(),
        this.activeFilter
      );
    }
  }

  public override setFilterCount(): void {
    this.filterCount =
      (this.activeFilter.names?.length || 0) +
      (this.activeFilter.titles?.length || 0) +
      (this.activeFilter.primaryAddresses?.length || 0) +
      (this.activeFilter.lotNumbers?.length || 0) +
      (this.activeFilter.statuses?.length || 0) +
      (this.activeFilter.frequencies?.length || 0) +
      (this.activeFilter.minimumScheduledAmount ? 1 : 0) +
      (this.activeFilter.maximumScheduledAmount ? 1 : 0) +
      (this.activeFilter.minimumLastModifiedDate ? 1 : 0) +
      (this.activeFilter.maximumLastModifiedDate ? 1 : 0);
  }

  public addName(): void {
    if (this.nameInput && !(this.nameInput.trim() === '')) {
      this.activeFilter.names?.push(this.nameInput);
      this.saveToStorage();
    }
    this.nameInput = '';
  }

  public removeName(index: number): void {
    this.activeFilter.names?.splice(index, 1);
    this.saveToStorage();
  }

  public addPropertyTitle(): void {
    if (this.propertyTitleInput && !(this.propertyTitleInput.trim() === '')) {
      this.activeFilter.titles?.push(this.propertyTitleInput);
      this.saveToStorage();
    }
    this.propertyTitleInput = '';
  }

  public removePropertyTitle(index: number): void {
    this.activeFilter.titles?.splice(index, 1);
    this.saveToStorage();
  }

  public addPropertyAddress(): void {
    if (this.propertyAddressInput && !(this.propertyAddressInput.trim() === '')) {
      this.activeFilter.primaryAddresses?.push(this.propertyAddressInput);
      this.saveToStorage();
    }
    this.propertyAddressInput = '';
  }

  public removePropertyAddress(index: number): void {
    this.activeFilter.primaryAddresses?.splice(index, 1);
    this.saveToStorage();
  }

  public addLotNumber(): void {
    if (this.lotNumberInput && !(this.lotNumberInput.trim() === '')) {
      this.activeFilter.lotNumbers?.push(this.lotNumberInput);
      this.saveToStorage();
    }
    this.lotNumberInput = '';
  }

  public removeLotNumber(index: number): void {
    this.activeFilter.lotNumbers?.splice(index, 1);
    this.saveToStorage();
  }

  public onStatusChange(event: any): void {
    this.activeFilter.statuses = event;
    this.saveToStorage();
  }

  public onFrequencyChange(event: any): void {
    this.activeFilter.frequencies = event;
    this.saveToStorage();
  }

  public onMinModifiedDateChange(): void {
    this.activeFilter.minimumLastModifiedDate = (this.minModifiedDateInput == "") ? undefined : this.minModifiedDateInput;
    this.saveToStorage();
  }

  public onMaxModifiedDateChange(): void {
    this.activeFilter.maximumLastModifiedDate = (this.maxModifiedDateInput == "") ? undefined : this.maxModifiedDateInput;
    this.saveToStorage();
  }
}
